import React from 'react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  message?: string;
  className?: string;
  variant?: 'default' | 'dashboard' | 'minimal' | 'skeleton';
  showIcon?: boolean;
  iconType?: 'spinner' | 'dots' | 'pulse' | 'bars';
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  message = 'در حال بارگذاری…',
  className = '',
  variant = 'default',
  showIcon = true,
  iconType = 'spinner'
}) => {
  // Size configurations
  const sizeClasses = {
    sm: {
      container: 'p-4',
      spinner: 'w-4 h-4',
      text: 'text-sm',
      icon: 'w-5 h-5'
    },
    md: {
      container: 'p-6',
      spinner: 'w-8 h-8',
      text: 'text-base',
      icon: 'w-6 h-6'
    },
    lg: {
      container: 'p-8',
      spinner: 'w-12 h-12',
      text: 'text-lg',
      icon: 'w-8 h-8'
    },
    xl: {
      container: 'p-12',
      spinner: 'w-16 h-16',
      text: 'text-xl',
      icon: 'w-10 h-10'
    }
  };

  const currentSize = sizeClasses[size];

  // Loading icons based on type
  const renderIcon = () => {
    const iconClasses = `${currentSize.spinner} transition-all`;
    
    switch (iconType) {
      case 'spinner':
        return (
          <div className={`${iconClasses} animate-loading-spin rounded-full border-4 border-border-primary border-t-primary-500`}></div>
        );
      
      case 'dots':
        return (
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"></div>
          </div>
        );
      
      case 'pulse':
        return (
          <div className={`${iconClasses} bg-gradient-to-r from-primary-500 via-accent-cyan to-primary-500 rounded-full animate-loading-pulse`}></div>
        );
      
      case 'bars':
        return (
          <div className="flex items-end gap-1">
            <div className="w-1 h-6 bg-primary-500 animate-loading-pulse [animation-delay:-0.4s]"></div>
            <div className="w-1 h-4 bg-accent-cyan animate-loading-pulse [animation-delay:-0.2s]"></div>
            <div className="w-1 h-8 bg-primary-500 animate-loading-pulse"></div>
            <div className="w-1 h-5 bg-accent-cyan animate-loading-pulse [animation-delay:-0.3s]"></div>
            <div className="w-1 h-7 bg-primary-500 animate-loading-pulse [animation-delay:-0.1s]"></div>
          </div>
        );
      
      default:
        return (
          <div className={`${iconClasses} animate-loading-spin rounded-full border-4 border-border-primary border-t-primary-500`}></div>
        );
    }
  };

  // Variant-specific rendering
  switch (variant) {
    case 'minimal':
      return (
        <div className={`flex flex-col items-center justify-center min-h-[200px] ${currentSize.container} ${className}`}>
          {showIcon && (
            <div className="mb-3">
              {renderIcon()}
            </div>
          )}
          {message && (
            <div className={`${currentSize.text} text-text-secondary text-center font-normal`}>
              {message}
            </div>
          )}
        </div>
      );

    case 'skeleton':
      return (
        <div className={`animate-enter-up space-y-6 ${className}`}>
          {/* Header Skeleton */}
          <div className="bg-bg-card border border-border-primary rounded-lg p-6 animate-loading-pulse">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-bg-tertiary rounded-lg animate-shimmer"></div>
              <div className="flex-1">
                <div className="h-6 bg-bg-tertiary rounded w-64 mb-2 animate-shimmer"></div>
                <div className="h-4 bg-bg-secondary rounded w-32 animate-shimmer"></div>
              </div>
            </div>
          </div>

          {/* KPI Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-bg-card border border-border-primary rounded-lg p-5 shadow-base animate-loading-pulse">
                <div className="h-4 bg-bg-tertiary rounded w-3/4 mb-4 animate-shimmer"></div>
                <div className="h-8 bg-bg-secondary rounded w-1/2 mx-auto animate-shimmer"></div>
              </div>
            ))}
          </div>

          {/* Chart Skeleton */}
          <div className="bg-bg-card border border-border-primary rounded-lg p-6 animate-loading-pulse">
            <div className="h-6 bg-bg-tertiary rounded w-48 mb-6 animate-shimmer"></div>
            <div className="h-80 bg-bg-secondary rounded animate-shimmer"></div>
          </div>

          {/* Loading Indicator */}
          <div className="flex justify-center items-center py-8">
            <div className="text-center">
              <div className="relative w-12 h-12 mx-auto mb-4">
                <div className="absolute inset-0 border-4 border-border-primary rounded-full"></div>
                <div className="absolute inset-0 border-4 border-primary-500 border-t-transparent rounded-full animate-loading-spin"></div>
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">در حال بارگذاری داده‌ها</h3>
              <p className="text-text-secondary">لطفاً صبر کنید…</p>
            </div>
          </div>
        </div>
      );

    case 'dashboard':
      return (
        <div className={`animate-enter-up space-y-6 ${className}`}>
          {/* Dashboard Loading Header */}
          <div className="bg-bg-card border border-border-primary rounded-lg p-6 shadow-base animate-loading-pulse">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-cyan rounded-lg flex items-center justify-center animate-float">
                <span className="text-3xl">📊</span>
              </div>
              <div className="flex-1">
                <div className="h-6 bg-bg-tertiary rounded w-64 mb-2 animate-shimmer"></div>
                <div className="h-4 bg-bg-secondary rounded w-32 animate-shimmer"></div>
              </div>
            </div>
          </div>

          {/* Loading Progress */}
          <div className="bg-bg-card border border-border-primary rounded-lg p-8 shadow-base">
            <div className="text-center">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 border-4 border-border-primary rounded-full"></div>
                <div className="absolute inset-0 border-4 border-primary-500 border-t-transparent rounded-full animate-loading-spin"></div>
                <div className="absolute inset-2 border-2 border-accent-cyan border-b-transparent rounded-full animate-loading-spin [animation-direction:reverse] [animation-duration:1.5s]"></div>
              </div>
              
              <h3 className="text-xl font-bold text-text-primary mb-3 animate-loading-pulse">
                {message || 'در حال بارگذاری داشبورد'}
              </h3>
              
              <div className="flex items-center justify-center gap-2 text-text-secondary mb-4">
                <div className="loading-dots">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
                <span className="text-sm animate-enter-up">لطفاً صبر کنید…</span>
              </div>

              {/* Progress Bar */}
              <div className="w-full max-w-md mx-auto">
                <div className="h-2 bg-bg-tertiary rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary-500 to-accent-cyan rounded-full animate-loading-pulse"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats Placeholder */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`bg-bg-card border border-border-primary rounded-lg p-5 shadow-base animate-loading-pulse stagger-${i + 1}`}>
                <div className="h-4 bg-bg-tertiary rounded w-3/4 mb-4 animate-shimmer"></div>
                <div className="h-8 bg-bg-secondary rounded w-1/2 mx-auto animate-shimmer"></div>
                <div className="h-3 bg-bg-tertiary rounded w-1/3 mx-auto mt-2 animate-shimmer"></div>
              </div>
            ))}
          </div>
        </div>
      );

    default:
      return (
        <div className={`flex flex-col items-center justify-center ${currentSize.container} ${className}`}>
          {/* Main Icon */}
          {showIcon && (
            <div className="mb-4 relative">
              {renderIcon()}
              {size === 'lg' || size === 'xl' ? (
                <div className="absolute -inset-2 border border-primary-500 rounded-full opacity-30 animate-ping"></div>
              ) : null}
            </div>
          )}
          
          {/* Message */}
          {message && (
            <div className={`${currentSize.text} text-text-primary text-center font-medium animate-fade-in-up`}>
              {message}
            </div>
          )}
          
          {/* Subtitle for larger sizes */}
          {(size === 'lg' || size === 'xl') && (
            <div className="text-sm text-text-secondary text-center mt-2 animate-fade-in-up stagger-1">
              لطفاً صبر کنید…
            </div>
          )}
        </div>
      );
  }
};

export default LoadingSpinner;